/**
 * Saved Portfolio Store — the user's own books, served by the gateway.
 *
 * **This store used to talk to Firestore directly, and that was [[BUGS #152]]
 * and [[#155]].** The browser's Firestore client resolves to the PRODUCTION
 * project on every deploy, because Auth is deliberately shared and
 * `REACT_APP_FIREBASE_PROJECT_ID` is pinned with it. The backend reads
 * `users/{uid}/saved_portfolios` from the project it runs in. On staging those
 * are two different databases, so `set_automation` refused every toggle with
 * "Portfolio not found or access denied" and the Signals daily loop watched an
 * empty collection while telling subscribers otherwise.
 *
 * Every read and every write now goes through `portfolioService`, so a
 * portfolio belongs to the environment that created it. **Reads had to move
 * with the writes** — writing to staging while still reading production would
 * have made a saved portfolio vanish the instant it was saved.
 *
 * **The caps below are a UX courtesy, not the rule.** The rule is
 * `_entitlements.max_portfolios_for` / `portfolio_cap_for`, enforced in
 * `_saved.py` and returned as a 402 carrying the sentence to show. These local
 * copies exist only so the reader is told before a round-trip; when the server
 * disagrees, the server wins and its sentence is what gets displayed.
 */
import { create } from 'zustand';
import { useSubscriptionStore } from './subscriptionStore';
import { logger } from '../services/loggingService';
import { getErrorMessage } from '../utils/validation';
import analyticsService from '../services/analytics';
import portfolioService, { SavedPortfolioDto } from '../services/portfolioService';
import { auth } from '../firebase';

export interface Holding {
  symbol: string;
  shares: number;
  costBasis?: number;
  exchange?: string;
  // Asset-class metadata derived during row reconciliation (LLM + FMP
  // `isEtf` flag). Optional because manual / CSV entry may not carry it;
  // server-side falls back to FMP-derived classification when absent.
  // When present, the stress engine uses these directly without re-deriving
  // — keeping client intent and server result aligned.
  asset_type?: 'stock' | 'etf' | 'crypto';
  etf_category?: string | null;
  /** Per-row cost-basis currency the user originally paid in (e.g. 'AUD'
   *  for BHP.AX, 'USD' for AAPL). Preserves the user's intent across
   *  save/load so they don't see the converted USD figure when they
   *  revisit the portfolio. Defaults to 'USD' when absent. */
  cost_basis_currency?: string;
}

export interface SavedPortfolio {
  id: string;
  name: string;
  holdings: Holding[];
  createdAt: Date;
  /** Document schema version. Missing on pre-2026-06-14 docs (treated
   *  as v1 by the load path). Bump only on a breaking change: adding
   *  an optional field is NOT breaking. Renaming a field, changing
   *  semantics, or removing a field IS breaking and gets a new version
   *  + a migration in the load path's `migrateToCurrentSchema()`. */
  schemaVersion?: number;
  /** Where this book came from (ROADMAP A.21.2). A proposal saved from the
   *  rebalance simulation is a HYPOTHESIS under assessment — the reader
   *  deciding which book is real money gets that on the record, not in a name
   *  string a rename would erase. Server-allowlisted both ways: a hand-edited
   *  doc cannot invent a provenance. */
  origin?: { kind: 'simulation'; strategy?: string } | null;
  /** Whether the Signals daily loop watches this book.
   *
   *  Server-owned: the backend sets it at subscribe time
   *  (`_entitlements.auto_enable_monitoring`) and on the `set_automation`
   *  action, and the daily scan reads it. It was absent from this model
   *  entirely until 2026-08-23, which is why the only place the product
   *  displayed it was the admin console — a subscriber could not see, move
   *  or stop the monitoring they were paying for (ROADMAP 2.9).
   *
   *  Optional, so pre-existing docs read cleanly and no schema bump applies. */
  automated?: boolean;
}

/**
 * Current portfolio schema version. Written to every new/updated doc.
 * Bump rules:
 *   - Adding an optional field      → DO NOT bump (backward-compat)
 *   - Renaming a field              → BUMP + migration
 *   - Changing field semantics      → BUMP + migration
 *   - Removing a field              → BUMP + migration
 *
 * Migration history:
 *   v1 (implicit, pre-2026-06-14): { symbol, shares, costBasis?, exchange? }
 *   v2 (2026-06-14): adds asset_type, etf_category, cost_basis_currency
 *                    — all optional, so v1 docs read cleanly without migration
 */
export const PORTFOLIO_SCHEMA_VERSION = 2;

/** Which plan limit stopped the last save, so the page can offer the way
 *  forward instead of a guess ([[BUGS #509]]). */
export type SaveLimit =
  | { kind: 'portfolios'; cap: number; names: string[] }
  | { kind: 'holdings'; cap: number; count: number };

/** Strategist is the top self-serve plan — past it there is no plan to offer. */
export const morePlansExist = (): boolean => useSubscriptionStore.getState().plan !== 'strategist';

/** The sentence for a limit: what the limit is, what is using it, what to do. */
export const limitSentence = (limit: SaveLimit, offerPlans: boolean = morePlansExist()): string => {
  const more = offerPlans ? ', or see plans that keep more' : '';
  if (limit.kind === 'holdings') {
    return `This portfolio has ${limit.count} holdings, and your plan keeps up to ${limit.cap} in one portfolio. `
      + `Remove some before saving${more}.`;
  }
  if (limit.cap === 1) {
    const which = limit.names[0] ? `"${limit.names[0]}"` : 'one';
    return `Your plan keeps 1 portfolio, and you already have ${which}. `
      + `Delete or replace it in your portfolios to save this one${more}.`;
  }
  return `Your plan keeps ${limit.cap} portfolios, and all ${limit.cap} are in use. `
    + `Delete one in your portfolios to save this one${more}.`;
};

interface SavedPortfolioState {
  portfolios: SavedPortfolio[];
  isLoading: boolean;
  error: string | null;
  limitHit: SaveLimit | null;
  abortController: AbortController | null;  // Prevents memory leaks from unmounted components
  loadPortfolios: (userId: string) => Promise<void>;
  savePortfolio: (userId: string, name: string, holdings: Holding[],
    origin?: SavedPortfolio['origin']) => Promise<string | null>;
  renamePortfolio: (userId: string, portfolioId: string, newName: string) => Promise<void>;
  /** Turn the Signals daily loop on or off for one book (ROADMAP 2.9).
   *  The SERVER decides — the per-plan cap and the entitlement are enforced
   *  there — so this returns the server's plain-English reason on refusal
   *  rather than pre-judging it in the client. */
  setAutomation: (portfolioId: string, enabled: boolean) =>
    Promise<{ ok: boolean; reason?: string }>;
  updatePortfolio: (userId: string, portfolioId: string, name: string, holdings: Holding[]) => Promise<void>;
  deletePortfolio: (userId: string, portfolioId: string) => Promise<void>;
  getPortfolio: (portfolioId: string) => SavedPortfolio | undefined;
  cleanup: () => void;  // Cancel pending operations on unmount
}

// The owner's plan model (2026-08-22), and these must equal the backend's
// `repositories/entitlements.MAX_PORTFOLIOS` / `PORTFOLIO_CAPS.max_holdings` — the
// cross-language test in __tests__/portfolioCaps.test.ts reads both files and
// fails on drift.
//
// These are a COURTESY, not the boundary. Since [[BUGS #152]] the same caps are
// enforced in `_saved.py` against the plan the server reads, so a client that
// skipped this check is refused with a 402 anyway. They stay because telling a
// reader "your plan keeps 2" before a round-trip is kinder than after one.
// Enforced on CREATE only: books that exceed a newly-lowered cap are kept.
const MAX_PORTFOLIOS: Record<string, number> = { free: 1, signals: 2, strategist: 5 };
// `repositories/entitlements.AUTOMATED_PORTFOLIO_CAPS` — how many books the daily loop
// watches. Strategist rose to 2 on 2026-09-05 ([[ROADMAP A.51]]), retiring the
// 'one book on every paid tier' rule that UI copy used to hardcode.
const MAX_MONITORED: Record<string, number> = { free: 0, signals: 1, strategist: 2 };
const MAX_HOLDINGS: Record<string, number> = { free: 50, signals: 100, strategist: 100 };

/** The caps that apply to the signed-in reader's plan. Exported so UI copy
 *  states the reader's ACTUAL limits — the hub used to hardcode "Up to 20
 *  portfolios, 300 holdings each", which was wrong on both numbers and wrong in
 *  kind: the cap is a plan benefit, not one flat rule (owner, 2026-08-30). */
export const capsForPlan = () => {
  const plan = useSubscriptionStore.getState().plan;
  return {
    portfolios: MAX_PORTFOLIOS[plan] ?? MAX_PORTFOLIOS.free,
    holdings: MAX_HOLDINGS[plan] ?? MAX_HOLDINGS.free,
    // Exported for the same reason as the other two: so the sentence beside the
    // control states the reader's ACTUAL limit instead of a number someone typed
    // once ([[BUGS #208]], and #254's shape — a correct table beside prose that
    // was not).
    monitored: MAX_MONITORED[plan] ?? MAX_MONITORED.free,
  };
};

/**
 * Forward-migrate a Firestore doc to the current schema shape. v1 docs
 * are missing schemaVersion + the v2 optional fields — the v2 fields
 * default to undefined, which downstream consumers (toSavedHolding /
 * fromSavedHolding mappers) already handle. Future breaking changes:
 * branch on `inDoc.schemaVersion ?? 1` and apply per-version transforms.
 */
export function migrateToCurrentSchema(inDoc: Record<string, unknown>): {
  name: string;
  holdings: Holding[];
  createdAt: unknown;
  schemaVersion: number;
  automated: boolean;
  origin: SavedPortfolio['origin'];
} {
  const version = typeof inDoc.schemaVersion === 'number' ? inDoc.schemaVersion : 1;
  // No structural transforms needed for v1 → v2 because every v2 field
  // is optional. When a future version requires a real migration, add a
  // branch here BEFORE the return.
  //
  // Defensive: validate `holdings` is actually an array. A corrupt or
  // hand-edited doc with `holdings: "AAPL"` (string) would silently
  // pass the `as Holding[]` cast and crash downstream `.map()` calls.
  const holdings = Array.isArray(inDoc.holdings)
    ? (inDoc.holdings as Holding[])
    : [];
  return {
    name: (inDoc.name as string) || 'Untitled',
    holdings,
    createdAt: inDoc.createdAt,
    schemaVersion: version,
    // Server-owned (ROADMAP 2.9). Read as a strict boolean: absent means not
    // watched, and only `true` means watched — a truthy string from a
    // hand-edited doc must not read as "we are monitoring this".
    automated: inDoc.automated === true,
    // Provenance chip (A.21.2). Only the server-allowlisted kind survives —
    // same strictness as `automated`, same reason.
    origin:
      typeof inDoc.origin === 'object' && inDoc.origin !== null &&
      (inDoc.origin as { kind?: unknown }).kind === 'simulation'
        ? {
            kind: 'simulation',
            ...(typeof (inDoc.origin as { strategy?: unknown }).strategy === 'string'
              ? { strategy: (inDoc.origin as { strategy: string }).strategy }
              : {}),
          }
        : null,
  };
}

/**
 * The caller's ID token, or `null` when signed out.
 *
 * Read fresh on every call so an expired token is invisible to callers — the
 * same shape `llmChatService.chatHeaders` uses.
 */
const idToken = async (): Promise<string | null> => {
  try {
    const user = auth.currentUser;
    return user ? await user.getIdToken() : null;
  } catch {
    // A firebase handle that cannot answer is the same as being signed out
    // for our purposes, and it must not become an unhandled rejection.
    return null;
  }
};

/**
 * Every call here needs a signed-in user, so a missing token means "don't ask".
 *
 * **This is a correctness rule that also stopped a self-inflicted CI outage.**
 * These portfolios used to be read straight from Firestore, which is mocked in
 * component tests. Routing them through the gateway turned the same call into a
 * real HTTP request — so every suite that rendered a portfolio screen started
 * making a live call to the staging gateway and waiting on it. The frontend job
 * went from minutes to a 14-minute timeout.
 *
 * Returning early without a token fixes both halves: an anonymous visitor has no
 * saved portfolios, so the request could only ever have come back 401, and a
 * test that never signed anyone in makes no network call at all.
 */
const signedOut = async (): Promise<boolean> => !(await idToken());

/**
 * A wire date the UI can render.
 *
 * `new Date(x)` does NOT throw on nonsense — it returns `Invalid Date`, which
 * survives all the way to the screen and shows up there as the literal words
 * "Invalid Date". That is [[BUGS #156]] in the notification bell, and the same
 * shape reaches here from any document written before the field existed. So the
 * parse is checked, and an unusable value falls back to now.
 */
const parseDate = (raw: string | null | undefined): Date => {
  const parsed = raw ? new Date(raw) : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : new Date();
};

/** One gateway row as the store holds it. */
const fromDto = (dto: SavedPortfolioDto): SavedPortfolio => {
  const migrated = migrateToCurrentSchema(dto as unknown as Record<string, unknown>);
  return {
    id: dto.id,
    name: migrated.name,
    holdings: migrated.holdings,
    // ISO text over the wire — there is no Firestore `Timestamp` to unwrap.
    createdAt: parseDate(dto.createdAt),
    schemaVersion: migrated.schemaVersion,
    automated: migrated.automated,
    origin: migrated.origin,
  };
};

/**
 * What to show a reader when a call fails.
 *
 * A REFUSAL is not an outage. `makeRequest` throws on any non-2xx, so a 402
 * carrying the plan-cap sentence arrives here as an `Error` whose message IS
 * that sentence — show it. [[BUGS #155]] is the precedent: a 403 was being
 * reported as "could not reach the server", which sent the reader to check
 * their wifi while the real answer sat on the response.
 */
const reasonFrom = (err: unknown, fallback: string): string =>
  // `getErrorMessage` already takes the fallback; the guard is for an Error
  // thrown with an empty message, which would otherwise render as blank space
  // where the explanation should be.
  getErrorMessage(err, fallback).trim() || fallback;

export const useSavedPortfolioStore = create<SavedPortfolioState>((set, get) => ({
  portfolios: [],
  isLoading: false,
  error: null,
  limitHit: null,
  abortController: null,

  loadPortfolios: async (_userId: string) => {
    // `_userId` is ignored: the server takes the owner from the bearer token.
    // A uid the caller chooses was never an authorization decision, and now it
    // is not even a routing one. Kept in the signature so the ~10 call sites
    // are untouched.
    const { abortController: oldController } = get();
    oldController?.abort();
    const abortController = new AbortController();

    const token = await idToken();
    if (!token) {
      // Nobody is signed in: there is nothing to load, and asking would be a
      // guaranteed 401. See `signedOut`.
      //
      // **Keep the existing array when it is already empty.** Writing a fresh
      // `[]` changes the reference on every call, and `portfolios` is read
      // through a zustand selector compared with `Object.is` — so a component
      // whose effect depends on an unstable value (a `user` object rebuilt each
      // render is the common one) re-renders, re-runs the effect, calls this
      // again, and spins forever.
      //
      // Not hypothetical: it deadlocked `EmailConnectionCheck` and, in a full
      // run, the entire frontend job. The Firestore path this replaced never
      // touched `portfolios` on failure, so the loop had no fuel.
      const { portfolios } = get();
      set({
        abortController,
        isLoading: false,
        error: null,
        ...(portfolios.length > 0 ? { portfolios: [] } : {}),
      });
      return;
    }

    set({ abortController, isLoading: true, error: null });

    try {
      const result = await portfolioService.listSavedPortfolios(token);
      if (abortController.signal.aborted) return;
      set({ portfolios: (result?.portfolios || []).map(fromDto), isLoading: false });
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      logger.error('Failed to load portfolios', { error: getErrorMessage(err) });
      // Deliberately NOT clearing `portfolios` — an outage that renders as
      // "you have no portfolios" reads as data loss to the person it happens to.
      set({ error: 'Failed to load portfolios', isLoading: false });
    }
  },

  savePortfolio: async (_userId: string, name: string, holdings: Holding[], origin?: SavedPortfolio['origin']) => {
    const { portfolios } = get();

    // Told early, out of courtesy — the server enforces the same caps.
    const caps = capsForPlan();
    set({ limitHit: null });
    if (portfolios.length >= caps.portfolios) {
      const limitHit: SaveLimit = { kind: 'portfolios', cap: caps.portfolios, names: portfolios.map((p) => p.name) };
      set({ error: limitSentence(limitHit), limitHit });
      return null;
    }
    if (holdings.length > caps.holdings) {
      const limitHit: SaveLimit = { kind: 'holdings', cap: caps.holdings, count: holdings.length };
      set({ error: limitSentence(limitHit), limitHit });
      return null;
    }

    if (await signedOut()) {
      set({ error: 'Sign in to save a portfolio.' });
      return null;
    }

    set({ isLoading: true, error: null });
    try {
      const result = await portfolioService.createSavedPortfolio(
        name.trim() || 'Untitled Portfolio', holdings, await idToken(), origin,
      );
      const portfolioId = result?.portfolio_id;
      if (!portfolioId) throw new Error(result?.error || 'Failed to save portfolio');

      await get().loadPortfolios(_userId);
      logger.logCrud('create', 'portfolio', portfolioId, true);
      analyticsService.trackPortfolioSaved(portfolioId, holdings.length, true);
      return portfolioId;
    } catch (err) {
      const message = reasonFrom(err, 'Failed to save portfolio');
      logger.logCrud('create', 'portfolio', '', false, message);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  setAutomation: async (portfolioId: string, enabled: boolean) => {
    // Optimistic, then reconciled: the toggle answers instantly because the
    // reader is deciding whether their money is doing anything, and a control
    // that lags reads as broken. A refusal rolls it back and says why.
    const before = get().portfolios;
    set({
      portfolios: before.map(p => (p.id === portfolioId ? { ...p, automated: enabled } : p)),
    });
    try {
      const token = await idToken();
      if (!token) {
        set({ portfolios: before });
        return { ok: false, reason: 'Sign in to change daily monitoring.' };
      }
      const res = await portfolioService.setPortfolioAutomation(
        portfolioId, enabled, token,
      );
      if (res?.success) return { ok: true };
      set({ portfolios: before });
      // The server writes the cap/upsell sentence for this exact moment
      // ([[BUGS #24]]) — show it rather than a generic failure.
      return { ok: false, reason: res?.error || 'Could not change daily monitoring.' };
    } catch (e) {
      set({ portfolios: before });
      return {
        ok: false,
        reason: reasonFrom(e, 'Could not change daily monitoring. Try again in a moment.'),
      };
    }
  },

  renamePortfolio: async (_userId: string, portfolioId: string, newName: string) => {
    const name = newName.trim() || 'Untitled Portfolio';
    if (await signedOut()) {
      set({ error: 'Sign in to rename a portfolio.' });
      return;
    }
    set({ isLoading: true, error: null });
    try {
      await portfolioService.updateSavedPortfolio(portfolioId, { name }, await idToken());
      set((state) => ({
        portfolios: state.portfolios.map(p => (p.id === portfolioId ? { ...p, name } : p)),
        isLoading: false,
      }));
      logger.logCrud('update', 'portfolio', portfolioId, true);
    } catch (err) {
      const message = reasonFrom(err, 'Failed to rename portfolio');
      logger.logCrud('update', 'portfolio', portfolioId, false, message);
      set({ error: message, isLoading: false });
    }
  },

  updatePortfolio: async (_userId: string, portfolioId: string, name: string, holdings: Holding[]) => {
    if (holdings.length > capsForPlan().holdings) {
      set({ error: `Your plan allows up to ${capsForPlan().holdings} holdings per portfolio.` });
      return;
    }

    const cleanedName = name.trim() || 'Untitled Portfolio';
    if (await signedOut()) {
      set({ error: 'Sign in to update a portfolio.' });
      return;
    }
    set({ isLoading: true, error: null });
    try {
      await portfolioService.updateSavedPortfolio(
        portfolioId, { name: cleanedName, holdings }, await idToken(),
      );
      set((state) => ({
        portfolios: state.portfolios.map(p =>
          p.id === portfolioId ? { ...p, name: cleanedName, holdings } : p
        ),
        isLoading: false,
      }));
      logger.logCrud('update', 'portfolio', portfolioId, true);
    } catch (err) {
      const message = reasonFrom(err, 'Failed to update portfolio');
      logger.logCrud('update', 'portfolio', portfolioId, false, message);
      set({ error: message, isLoading: false });
    }
  },

  deletePortfolio: async (_userId: string, portfolioId: string) => {
    if (await signedOut()) {
      set({ error: 'Sign in to delete a portfolio.' });
      return;
    }
    set({ isLoading: true, error: null });
    try {
      await portfolioService.deleteSavedPortfolio(portfolioId, await idToken());
      set((state) => ({
        portfolios: state.portfolios.filter(p => p.id !== portfolioId),
        isLoading: false,
      }));
      logger.logCrud('delete', 'portfolio', portfolioId, true);
      analyticsService.trackPortfolioDeleted(portfolioId);
    } catch (err) {
      const message = reasonFrom(err, 'Failed to delete portfolio');
      logger.logCrud('delete', 'portfolio', portfolioId, false, message);
      set({ error: message, isLoading: false });
    }
  },

  getPortfolio: (portfolioId: string) => {
    return get().portfolios.find(p => p.id === portfolioId);
  },

  /**
   * Cancel pending operations (call on component unmount to prevent memory leaks)
   */
  cleanup: () => {
    const { abortController } = get();
    abortController?.abort();
    set({ abortController: null });
  }
}));
